import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { OwnerLayout } from '../../components/layouts/OwnerLayout';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  ClipboardCheck, User, MapPin, Calendar, Clock, CheckSquare, 
  Thermometer, Package, Camera, X, Save, AlertTriangle, ChevronLeft, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const CompleteJob = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { jobId } = useParams();
  
  const [job, setJob] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [startedAt] = useState(new Date().toISOString());
  const [reportType, setReportType] = useState('normal');
  const [showBeforeAfter, setShowBeforeAfter] = useState(false);
  
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({
    checklist: [],
    parameters: [],
    materials_used: [],
    observations: '',
    photos: [],
    manual_duration: null,
    send_report_email: false
  });

  useEffect(() => {
    fetchData();
  }, [jobId]);

  const fetchData = async () => {
    try {
      const [jobRes, templatesRes, materialsRes] = await Promise.all([
        api.get(`/jobs/${jobId}`),
        api.get('/report-templates'),
        api.get('/materials')
      ]);
      
      setJob(jobRes.data);
      setTemplates(templatesRes.data);
      setMaterials(materialsRes.data);
      
      // If job has a template assigned, load it
      if (jobRes.data.template_id) {
        const template = templatesRes.data.find(t => t.id === jobRes.data.template_id);
        if (template) {
          applyTemplate(template);
        }
      } else {
        // Load default template
        const defaultTemplate = templatesRes.data.find(t => t.is_default);
        if (defaultTemplate) {
          applyTemplate(defaultTemplate);
        }
      }
    } catch (error) {
      toast.error('Error al cargar el trabajo');
      navigate('/my-jobs');
    } finally {
      setLoading(false);
    }
  };

  const applyTemplate = (template) => {
    setSelectedTemplate(template);
    setFormData(prev => ({
      ...prev,
      checklist: (template.checklist_items || []).map((item, idx) => ({
        id: idx,
        item: item,
        completed: false
      })),
      parameters: (template.parameters || []).map((param, idx) => ({
        id: idx,
        name: param.name || param,
        value: '',
        value_before: '',
        value_after: '',
        unit: param.unit || ''
      }))
    }));
  };

  const handleChecklistToggle = (id) => {
    setFormData(prev => ({
      ...prev,
      checklist: prev.checklist.map(item =>
        item.id === id ? { ...item, completed: !item.completed } : item
      )
    }));
  };

  const handleParameterChange = (id, value, field = 'value') => {
    setFormData(prev => ({
      ...prev,
      parameters: prev.parameters.map(param =>
        param.id === id ? { ...param, [field]: value } : param
      )
    }));
  };

  const handleAddMaterial = (materialId) => {
    const material = materials.find(m => m.id === materialId);
    if (!material) return;
    
    const existing = formData.materials_used.find(m => m.material_id === materialId);
    if (existing) {
      toast.info('Este material ya está añadido');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      materials_used: [...prev.materials_used, {
        material_id: materialId,
        name: material.name,
        quantity: 1,
        unit_price: material.unit_price || 0
      }]
    }));
  };

  const handleMaterialQuantityChange = (materialId, quantity) => {
    setFormData(prev => ({
      ...prev,
      materials_used: prev.materials_used.map(m =>
        m.material_id === materialId ? { ...m, quantity: parseInt(quantity) || 0 } : m
      )
    }));
  };

  const handleRemoveMaterial = (materialId) => {
    setFormData(prev => ({
      ...prev,
      materials_used: prev.materials_used.filter(m => m.material_id !== materialId)
    }));
  };

  const handlePhotoCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (formData.photos.length >= 5) {
      toast.error('Máximo 5 fotos por parte');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, event.target?.result]
      }));
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Transform data for API
      const reportData = {
        job_id: jobId,
        client_id: job.client_id,
        checklist: formData.checklist.map(item => ({
          label: item.item,
          checked: item.completed
        })),
        parameters: formData.parameters.map(p => ({
          name: p.name,
          value: showBeforeAfter ? '' : p.value,
          value_before: showBeforeAfter ? p.value_before : null,
          value_after: showBeforeAfter ? p.value_after : null,
          unit: p.unit || ''
        })),
        materials_used: formData.materials_used.map(m => ({
          material_id: m.material_id,
          quantity: m.quantity
        })),
        observations: formData.observations,
        photos: formData.photos,
        started_at: startedAt,
        manual_duration: formData.manual_duration,
        send_report_email: formData.send_report_email,
        report_type: reportType
      };
      
      await api.post('/work-reports', reportData);
      
      toast.success(reportType === 'incidencia' ? '¡Incidencia registrada!' : '¡Parte completado correctamente!', {
        description: 'El trabajo ha sido finalizado.'
      });
      
      navigate('/my-jobs');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al completar el parte'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <OwnerLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
        </div>
      </OwnerLayout>
    );
  }

  if (!job) {
    return (
      <OwnerLayout>
        <div className="text-center py-12">
          <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h2 className="text-xl font-bold text-slate-900">Trabajo no encontrado</h2>
          <Button onClick={() => navigate('/my-jobs')} className="mt-4">
            Volver a Mis Trabajos
          </Button>
        </div>
      </OwnerLayout>
    );
  }

  return (
    <OwnerLayout>
      <div className="space-y-6" data-testid="complete-job-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Button 
              variant="ghost" 
              onClick={() => navigate('/my-jobs')}
              className="mb-2 -ml-2"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Volver
            </Button>
            <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ClipboardCheck className="w-7 h-7 text-sky-600" />
              Completar Trabajo
            </h1>
            <p className="text-slate-500">{job.title}</p>
          </div>
          {job.is_emergency && (
            <Badge className="bg-red-500 text-white self-start animate-pulse">
              <AlertTriangle className="w-4 h-4 mr-1" />
              URGENTE
            </Badge>
          )}
        </div>

        {/* Job Info */}
        <Card className="bg-gradient-to-r from-slate-50 to-slate-100">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Cliente</p>
                  <p className="font-medium">{job.client_name || 'Sin nombre'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Dirección</p>
                  <p className="font-medium truncate">{job.client_address || 'Sin dirección'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Fecha</p>
                  <p className="font-medium">{format(new Date(job.scheduled_date), "d 'de' MMMM yyyy", { locale: es })}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Hora</p>
                  <p className="font-medium">{job.scheduled_time || 'Sin especificar'}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Template Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5" />
              Plantilla
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select 
              value={selectedTemplate?.id || ''} 
              onValueChange={(id) => {
                const template = templates.find(t => t.id === id);
                if (template) applyTemplate(template);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona una plantilla" />
              </SelectTrigger>
              <SelectContent>
                {templates.map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name} {t.is_default && '(predeterminada)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Report Type Selection */}
        <Card className={reportType === 'incidencia' ? 'border-red-300 bg-red-50' : ''}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className={`w-5 h-5 ${reportType === 'incidencia' ? 'text-red-600' : 'text-slate-400'}`} />
              Tipo de Parte
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-3">
              <Button
                type="button"
                variant={reportType === 'normal' ? 'default' : 'outline'}
                className={reportType === 'normal' ? 'bg-sky-600 hover:bg-sky-700' : ''}
                onClick={() => setReportType('normal')}
              >
                Parte Normal
              </Button>
              <Button
                type="button"
                variant={reportType === 'incidencia' ? 'default' : 'outline'}
                className={reportType === 'incidencia' ? 'bg-red-600 hover:bg-red-700' : ''}
                onClick={() => setReportType('incidencia')}
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Incidencia
              </Button>
            </div>
            {reportType === 'incidencia' && (
              <p className="text-sm text-red-700">
                Las incidencias se muestran en rojo y sirven para reportar problemas o cosas que están mal.
              </p>
            )}
            
            {/* Before/After toggle */}
            <div className="flex items-center justify-between pt-2 border-t">
              <div>
                <Label className="text-sm font-medium">Valores Antes/Después</Label>
                <p className="text-xs text-slate-500">Registrar parámetros antes y después del tratamiento</p>
              </div>
              <Switch
                checked={showBeforeAfter}
                onCheckedChange={setShowBeforeAfter}
              />
            </div>
          </CardContent>
        </Card>

        {/* Checklist */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckSquare className="w-5 h-5" />
              Checklist
            </CardTitle>
            <CardDescription>
              {formData.checklist.filter(c => c.completed).length} de {formData.checklist.length} completadas
            </CardDescription>
          </CardHeader>
          <CardContent>
            {formData.checklist.length === 0 ? (
              <p className="text-slate-500 text-center py-4">Selecciona una plantilla para ver el checklist</p>
            ) : (
              <div className="space-y-3">
                {formData.checklist.map((item) => (
                  <div 
                    key={item.id}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-colors cursor-pointer ${
                      item.completed ? 'bg-green-50' : 'bg-slate-50 hover:bg-slate-100'
                    }`}
                    onClick={() => handleChecklistToggle(item.id)}
                  >
                    <Checkbox 
                      checked={item.completed}
                      onCheckedChange={() => handleChecklistToggle(item.id)}
                    />
                    <span className={item.completed ? 'line-through text-slate-400' : ''}>
                      {item.item}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Parameters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Thermometer className="w-5 h-5" />
              Parámetros
            </CardTitle>
          </CardHeader>
          <CardContent>
            {formData.parameters.length === 0 ? (
              <p className="text-slate-500 text-center py-4">Selecciona una plantilla para ver los parámetros</p>
            ) : showBeforeAfter ? (
              // Before/After mode
              <div className="space-y-4">
                {formData.parameters.map((param) => (
                  <div key={param.id} className="space-y-2 p-3 bg-slate-50 rounded-lg">
                    <Label className="font-medium">{param.name} {param.unit && `(${param.unit})`}</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">Antes</Label>
                        <Input
                          type="text"
                          value={param.value_before || ''}
                          onChange={(e) => handleParameterChange(param.id, e.target.value, 'value_before')}
                          placeholder="Valor antes"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Después</Label>
                        <Input
                          type="text"
                          value={param.value_after || ''}
                          onChange={(e) => handleParameterChange(param.id, e.target.value, 'value_after')}
                          placeholder="Valor después"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Normal mode
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {formData.parameters.map((param) => (
                  <div key={param.id} className="space-y-1">
                    <Label>{param.name} {param.unit && `(${param.unit})`}</Label>
                    <Input
                      type="text"
                      value={param.value}
                      onChange={(e) => handleParameterChange(param.id, e.target.value)}
                      placeholder={`Valor de ${param.name}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Materials */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="w-5 h-5" />
              Materiales Utilizados
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Select onValueChange={handleAddMaterial}>
              <SelectTrigger>
                <SelectValue placeholder="Añadir material..." />
              </SelectTrigger>
              <SelectContent>
                {materials.map(m => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name} - {m.unit_price?.toFixed(2) || '0.00'}€
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {formData.materials_used.length > 0 && (
              <div className="space-y-2">
                {formData.materials_used.map((material) => (
                  <div key={material.material_id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">{material.name}</p>
                      <p className="text-sm text-slate-500">{material.unit_price?.toFixed(2) || '0.00'}€/ud</p>
                    </div>
                    <Input
                      type="number"
                      min="1"
                      value={material.quantity}
                      onChange={(e) => handleMaterialQuantityChange(material.material_id, e.target.value)}
                      className="w-20"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveMaterial(material.material_id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Photos */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Fotos
            </CardTitle>
            <CardDescription>{formData.photos.length}/5 fotos</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {formData.photos.map((photo, index) => (
                <div key={index} className="relative aspect-square">
                  <img 
                    src={photo} 
                    alt={`Foto ${index + 1}`}
                    className="w-full h-full object-cover rounded-lg"
                  />
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute top-1 right-1 w-6 h-6"
                    onClick={() => removePhoto(index)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
              {formData.photos.length < 5 && (
                <label className="aspect-square border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-sky-500 hover:bg-sky-50 transition-colors">
                  <Camera className="w-8 h-8 text-slate-400 mb-2" />
                  <span className="text-sm text-slate-500">Añadir foto</span>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handlePhotoCapture}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Observations & Time */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Observaciones y Tiempo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={formData.observations}
              onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
              placeholder="Notas adicionales, problemas detectados..."
              rows={4}
            />
            
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-slate-500" />
                <Label className="text-sm font-medium">Duración del trabajo (opcional)</Label>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Si no lo indicas, se calculará automáticamente.
              </p>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  max="480"
                  value={formData.manual_duration || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    manual_duration: e.target.value ? parseInt(e.target.value) : null 
                  })}
                  placeholder="Ej: 45"
                  className="w-24"
                />
                <span className="text-sm text-slate-600">minutos</span>
              </div>
            </div>
            
            {/* Send email option - only if client has email */}
            {job.client_email && (
              <div className="flex items-center justify-between p-4 bg-sky-50 rounded-lg">
                <div>
                  <p className="font-medium">Enviar PDF por email</p>
                  <p className="text-sm text-slate-500">Se enviará a {job.client_email}</p>
                </div>
                <Switch
                  checked={formData.send_report_email}
                  onCheckedChange={(checked) => setFormData({ ...formData, send_report_email: checked })}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="sticky bottom-4 flex gap-4">
          <Button 
            variant="outline" 
            onClick={() => navigate('/my-jobs')}
            className="flex-1 sm:flex-none"
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Completar Parte
              </>
            )}
          </Button>
        </div>
      </div>
    </OwnerLayout>
  );
};

export default CompleteJob;
